import React, { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getSessionUserId } from '@/utils/sessionUserId';

import { CHATBOTS_WITH_NEW_DESIGN, DANTE_ICON, DEFAULT_CHAT_MESSAGES, LLM_MODEL_DEFAULT, STATUS } from '@/constants';
import formatEventData from '@/helpers/formatEventData';
import transformMessageToBubbleMessage from '@/helpers/transformMessageToBubbleMessage';
import useTaskHandler from '@/hooks/useTaskHandler';
import usePromptSuggestions from '@/hooks/usePromptSuggestions';
import {
  sendMessageEmbed,
  sendMessageInApp,
  createSharedFormData,
  requestTakeover,
  pollTakeover,
  showDynamicButtons,
} from '@/services/chat.service';
import * as conversationsService from '@/services/conversations.service';
import * as customizationService from '@/services/customization.service';
import * as humanHandoverService from '@/services/humanHandover';
import * as messagesService from '@/services/message.service';
import * as suggestionService from '@/services/suggestion.service';
import * as userService from '@/services/user.service';
import { useConversationStore } from '@/hooks/useConversationStore';
import useTaskStore from '@/stores/task/taskStore';

import NewChat from '../Chat/NewChat';
import OldChat from '../Chat';
import V3Chat from '@/v3/components/UI/Chat';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import * as ttsService from '@/services/tts.service';
import { usePreviousConversationsStore } from '@/stores/previousConversation/previousConversationStore';
import PropTypes from 'prop-types';
import { useUserStore } from '@/stores/user/userStore';
import * as modelService from '@/services/model.service';
import transformMessageToBubbleMessageHumanHandover from '@/helpers/transformMessageToBubbleMessageHumanHandover';
import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';
import { showNotificationWithSound, unlockAudioContext } from '@/helpers/notificationHelper';
import DSpinner from '@/components/Global/DSpinner';
import useHumanHandoverWebSocket from '@/hooks/useHumanHandoverWebSocket';
import VoicePreview from '@/components/Voice/VoicePreview';
import { WS_ENDPOINT_TYPES } from '@/services/websocket/voiceWebSocketUtils';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import featureCheck from '@/helpers/tier/featureCheck';
import {
  getStoredLeadGenStatus,
  setStoredLeadGenStatus,
  clearStoredLeadGenStatus
} from '@/utils/leadGenStorage';


class RetriableError extends Error {}
class FatalError extends Error {}

// Custom hook to get the conversation store with the correct chatbot ID and interface type

const ChatManager =  forwardRef(({
  config,
  hiddenConversation = false,
  isInApp = false,
  isDanteFaq = false,
  showMenuBtn = true,
  showCloseBtn = false,
  handleOpenDanteConversations,
  handleOpenDanteFaq,
  handleCloseButton,
  showInAppHeader = true,
  hiddenLlmSelector = false,
  initialShouldFetchCustomization = true,
  isPreviewMode = false,
  cookie,
  setCookie,
  forceRefetch,
  promptToSend,
  setPromptToSend,
  onPromptProcessed,
  isInHumanHandoverApp = false,
  isLiveAgentConversationResolved = false,
  liveAgentConversation = null,
  webSocketHook = null,
  showEmailTranscript = false,
  setShowEmailTranscript = () => {},
  onNewConversationCreated,
  setIsHumanHandover,
  setAgentName,
  setAgentTitle,
  setAgentAvatar,
  setAgentJoined,
  isDirectLink = false,
  showNewDesign = true,
}, ref) => {
  const [urlParams] = useSearchParams();
  
  // Determine the interface type based on props
  const interfaceType = isInApp ? 'in-app' : isInHumanHandoverApp ? 'human-handover' : isDirectLink ? 'direct-link' : 'bubble';
  
  // Use the conversation store with automatic namespacing
  const {
    currentConversation,
    setCurrentConversation,
    resetCurrentConversation
  } = useConversationStore(config?.kb_id, interfaceType);
  
  // Add a reference to track the previous chatbot ID
  const prevChatbotIdRef = useRef(config?.kb_id);
  // Add a reference to track the previous conversation ID
  const prevConversationIdRef = useRef(currentConversation?.id);
  
  // Handle cleanup when switching between chatbots
  useEffect(() => {
    const currentChatbotId = config?.kb_id;
    const prevChatbotId = prevChatbotIdRef.current;
    
    // If the chatbot ID has changed, reset the conversation
    if (prevChatbotId && currentChatbotId && prevChatbotId !== currentChatbotId) {
      resetCurrentConversation();
    }
    
    // Update the previous chatbot ID reference
    prevChatbotIdRef.current = currentChatbotId;
    
    // Cleanup function to handle unmounting or chatbot switching
    return () => {
      // If we're unmounting with a specific chatbot ID, make sure to clean up
      if (currentChatbotId) {
        // No need to reset here as we'll do it on mount if the ID changes
      }
    };
  }, [config?.kb_id, resetCurrentConversation, interfaceType, currentConversation]);

  const { addOpenedConversation, setLiveAgentConversationEvent, getLiveAgentConversationEvents, deleteLiveAgentConversationEvent, setFileUploadSetting, fileUploadSetting, setExplicitlyEnabledFileUpload, explicitlyEnabledFileUpload } = useHumanHandoverStore((state) => state);



  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const setUserData = useUserStore((state) => state.setUser);
  const userData = useUserStore((state) => state.user);
  const auth = useUserStore((state) => state.auth);
  const setSaveSignUpBtn = useChatbotStore((state) => state.setSaveSignUpBtn);

  const [customizationData, setCustomizationData] = useState(config);

  // Access parentUrl from URL search parameters
  const parentUrl = urlParams.get('parentUrl') || '';

  const [messages, setMessages] = useState([]);
  

  const [control, setControl] = useState(null);
  const [shouldFetchCustomization, setShouldFetchCustomization] = useState(
    initialShouldFetchCustomization
  );
  const [isAnswerLoading, setIsAnswerLoading] = useState(false);
  //show buttons in chat (suggested prompts, live agent, etc)
  const [showButtons, setShowButtons] = useState(true);
  const [interactingWithLiveAgent, setInteractingWithLiveAgent] =
    useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [hasAskedForName, setHasAskedForName] = useState(false);
  const [pollResponse, setPollResponse] = useState(false);
  const [isLiveAgentResolved, setIsLiveAgentResolved] = useState(isLiveAgentConversationResolved);
  const [agentImage, setAgentImage] = useState(null);
  const [sendEventConnected, setSendEventConnected] = useState(null);
  const queryStreamOpen = useRef(false);
  const wsRef = useRef(null);
  const activeConnectionIdRef = useRef(null);
  const liveAgentData = useHumanHandoverStore((state) => state.userOrganization);

  const timezone = useTimezoneStore((state) => state.timezone);
  
  // Initialize sound effects hook with customization data
  const { playLiveAgentMessageSound } = useSoundEffects({ customizationData });

  // Always initialize the WebSocket hook to maintain consistent hook order
  const internalWebSocketHook = useHumanHandoverWebSocket({
    token: config?.token,
    isInApp,
    isInHumanHandoverApp,
    wsRef,
        onMessageReceived: (parsedData) => {
      
      // Handle incoming message
      const newMessage = {
        role: isInHumanHandoverApp ? 'user' : 'assistant',
        content: parsedData.data.text,
        type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
        agent_name: parsedData.data.agent_name || liveAgentData?.name,
        agent_profile_pic: liveAgentData?.image || parsedData.data.agent_profile_pic,
        agent_role: liveAgentData?.agent_title || parsedData.data.agent_role,
        agent_title: liveAgentData?.agent_title || parsedData.data.agent_title,
        images: parsedData.data.images,
        date_created: parsedData?.data?.date_created || new Date()
      };


      handleMessagesOnChat('add', newMessage);

      if (isInHumanHandoverApp) {
        showNotificationWithSound('New Message', {
          body: 'User said: ' + parsedData.data.text,
          icon: DANTE_ICON,
        });
      } else {
        playLiveAgentMessageSound();
      }
    },
    onAgentJoined: ({ infoAgent }) => {
      console.log('infoAgent', infoAgent);
      setAgentName && setAgentName(infoAgent?.name || infoAgent?.agent_name);
      setAgentTitle && setAgentTitle(infoAgent?.agent_title);
      setAgentAvatar && setAgentAvatar(infoAgent?.image || infoAgent?.agent_profile_pic);
      setAgentJoined && setAgentJoined(true); // Set agent joined flag
      setIsHumanHandover && setIsHumanHandover(true);
      
      // Find and update the connecting message with fade out effect
      setMessages(prevMessages => {
        const updatedMessages = prevMessages.map(msg => {
          if (msg.isConnecting) {
            return {
              ...msg,
              isFadingOut: true // Flag to trigger fade out animation
            };
          }
          return msg;
        });
        return updatedMessages;
      });

      // After a short delay, replace the connecting message with the joined message
      setTimeout(() => {
        setMessages(prevMessages => {
          const messagesWithoutConnecting = prevMessages.filter(msg => !msg.isConnecting);
          
          // Add the agent joined message with fade in effect
          const agentJoinedMessage = {
            id: uuidv4(),
            type: 'info',
            content: `Team member ${infoAgent?.agent_name ? infoAgent?.agent_name : ''} has joined the chat`,
            isFadingIn: true // Flag to trigger fade in animation
          };
          
          return [...messagesWithoutConnecting, agentJoinedMessage];
        });

        // Remove the fade in flag after animation completes
        setTimeout(() => {
          setMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.isFadingIn) {
                const { isFadingIn, ...messageWithoutFlag } = msg;
                return messageWithoutFlag;
              }
              return msg;
            });
          });
        }, 300); // Match CSS animation duration
      }, 300); // Wait for fade out to complete

      // File upload settings will be fetched when agent joins via humanHandoverTakeConversation
      // Do not fetch here to avoid premature API calls before the Join button is clicked

      // For user side (shared chat bubble) - using store-based updates, no API polling needed
      if (!isInApp && !isInHumanHandoverApp && config?.token) {
       
      }
    },
    onAgentResolved: ({ infoAgent }) => {
      // Agent has resolved the conversation
      const newMessage = {
        type: 'info',
        content: `Conversation has been marked as resolved by team member ${infoAgent?.agent_name ? infoAgent?.agent_name : ''}. Thank you for using our service`,
      };
      handleMessagesOnChat('add', newMessage);
      setPollResponse(false);
      setIsHumanHandover && setIsHumanHandover(false);
      setIsLiveAgentResolved(true);
    }
  });

  // Use the provided webSocketHook or the internal one
  const {
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    sendMessage: sendWebSocketMessage,
    sendControlMessage: sendWebSocketControlMessage,
    sendTypingStart: sendWebSocketTypingStart,
    sendTypingStop: sendWebSocketTypingStop,
    isConnected: isWebSocketConnected,
    isTyping: isWebSocketTyping,
    typingUser: webSocketTypingUser,
  } = webSocketHook || internalWebSocketHook;
  const [notification, setNotification] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [questionIsQuickResponse, setQuestionIsQuickResponse] = useState(false);
  const [sourcesReady, setSourcesReady] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState([]);
  const [onGoingStream, setOnGoingStream] = useState(false);
  const [messageLoaded, setMessageLoaded] = useState(false);
  const [speechPlaying, setSpeechPlaying] = useState(false);
  const [speechPlayingID, setSpeechPlayingID] = useState('');
  const [played, setPlayed] = useState([]);
  const [hideFooter, setHideFooter] = useState(false);
  const synth = window.speechSynthesis;
  const voices = synth?.getVoices();

  const isAgentAvailable = useMemo(() => {
    if (config?.live_agent_always_active) {
      return true;
    }

    if (!config?.talk_to_live_agent || !config?.live_agent_list_of_schedules?.length) {
      return false;
    }

    try {
      // Get the configured timezone or default to UTC
      const timezone = config?.human_handover_timezone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'Europe/Belgrade';
      
      // Get current date/time in the configured timezone using Luxon
      const now = DateTime.now().setZone(timezone);
      const currentDay = now.weekdayLong;
      const currentTime = now.toFormat('HH:mm');

      const schedule = config.live_agent_list_of_schedules.find(s => s.day === currentDay);
      if (!schedule || !schedule.from || !schedule.to) {
        return false;
      }

      // Convert schedule times to DateTime objects in the configured timezone for comparison
      const scheduleStart = DateTime.fromFormat(schedule.from, 'HH:mm', { zone: timezone });
      const scheduleEnd = DateTime.fromFormat(schedule.to, 'HH:mm', { zone: timezone });
      const currentDateTime = DateTime.fromFormat(currentTime, 'HH:mm', { zone: timezone });

      return currentDateTime >= scheduleStart && currentDateTime <= scheduleEnd;
    } catch (error) {
      console.error('Error checking live agent availability:', error);
      return false;
    }
  }, [config?.talk_to_live_agent, config?.live_agent_list_of_schedules, config?.human_handover_timezone, config?.live_agent_always_active]);

  const [dynamicButtons, setDynamicButtons] = useState({
    show_calendly_url: (config?.show_calendly_at_the_start && config?.calendly_integration_enabled) || false,
    show_data_form: config?.show_data_collection_on_conversation_begin || false,
    show_live_agent: config?.talk_to_live_agent && (
      config?.show_live_agent_on_conversation_begin || isAgentAvailable
    ) || false,
  });



  // Initialize lead generation state with stored values (dynamic based on config)
  const initialLeadGenStatus = getStoredLeadGenStatus(config?.kb_id, config);

  const [isLeadGenMode, setIsLeadGenMode] = useState(
    config?.data_collection && !initialLeadGenStatus.completed && !initialLeadGenStatus.declined
      ? config?.show_data_collection_on_conversation_begin
      : false
  );
  const [leadGenDeclined, setLeadGenDeclined] = useState(initialLeadGenStatus.declined);
  const [leadGenCompleted, setLeadGenCompleted] = useState(initialLeadGenStatus.completed);
  const leadGenCompletedRef = useRef(initialLeadGenStatus.completed); // Synchronous flag for immediate checks
  const [leadGenInputs, setLeadGenInputs] = useState({
    input_id: null,
    question: null,
    input_type: null,
    input_options: [],
    input_label: null,
    show_options: true,
  });

  const [savedQuestion, setSavedQuestion] = useState(null);
  const [firstQuestion, setFirstQuestion] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleTask } = useTaskHandler();
  const [chatImageLoader, setChatImageLoader] = useState(false);
  const [hasMic, setHasMic] = useState(config?.has_mic);

  const addConversation = usePreviousConversationsStore(
    (state) => state.addConversation
  );

  const [sources, setSources] = useState([]);
  const [openSources, setOpenSources] = useState(false);
  const [showSources, setShowSources] = useState([]);

  const hasLoadedMessages = useRef(false);

  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showVoicePreview, setShowVoicePreview] = useState(false);
  const [isPreviousConversationHasLiveAgent, setIsPreviousConversationHasLiveAgent] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);


  // Use the prompt suggestions hook
  const {
    suggestions: suggestionPrompts,
    shouldShow: showSuggestionPrompts,
    isLoadingSuggestions,
    markAsShown,
    resetPrompts,
    onResponseComplete
  } = usePromptSuggestions({
    config: customizationData,
    conversationId: currentConversation?.id,
    isAnswerLoading,
    isLeadGenMode,
    interactingWithLiveAgent,
    isLiveAgentResolved,
    pollResponse,
    hasStartedConversation,
    showConsent,
    isInApp
  });

  /**
   * Creates a new conversation and returns its data.
   * @returns {Object|null} The conversation data or null if an error occurs.
   */
  const createConversation = useCallback(async () => {
    try {
      const res = await conversationsService.postCreateConversation({
        kb_id: config?.kb_id,
      });
      return res?.data || {};
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [config?.id]);

  /**
   * Creates a new conversation for embeds (bubble, direct link, and embed) and returns its data.
   * @returns {Object|null} The conversation data or null if an error occurs.
   */
  const createSharedConversation = useCallback(async () => {
    try {
      const res = await conversationsService.postSharedCreateConversation({
        kb_id: config?.kb_id,
        token: config?.token,
        client_full_name: urlParams.get('client_full_name'),
        client_email: urlParams.get('client_email'),
        from_page: urlParams.get('from_page')
      });
      return res.data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [config?.id]);

  const createNewConversation = useCallback(async () => {
    try {
      const newConversation = isInApp || isPreviewMode
        ? await createConversation()
        : await createSharedConversation();

      if (newConversation) {
        setCurrentConversation({ ...newConversation, type: 'new' });
        return newConversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  }, [createConversation, createSharedConversation, isInApp, setCurrentConversation, config?.kb_id, interfaceType]);

  /**
   * Finalizes the process of generating an answer and requests new suggestions.
   *
   * This function is called when the process of generating an answer is complete.
   * It closes the query stream and triggers a request for new suggestions based on the current messages.
   */
  const finishGeneratingAnswer = useCallback(
    (currectConversationId, isNewConversation = false) => {
      queryStreamOpen.current = false;
      onResponseComplete(currectConversationId);
      
      setIsAnswerLoading(false);

      // If this was a new conversation and we're in the app, notify parent with the conversation
      // that now has a title generated after the first message
      if (isNewConversation && isInApp && onNewConversationCreated) {
        // Wait a bit for the backend to generate the title, then notify parent
        setTimeout(async () => {
          try {
            // Fetch the updated conversation with the generated title
            const response = await conversationsService.getConversationsByKbId(config?.kb_id);
            const updatedConversation = response?.data?.results?.find(conv => conv.id === currectConversationId);

            if (updatedConversation) {
              onNewConversationCreated(updatedConversation);
            } else {
              // Fallback: notify with basic conversation data
              onNewConversationCreated({ id: currectConversationId });
            }
          } catch (error) {
            console.error('Error fetching updated conversation:', error);
            // Fallback: notify with basic conversation data
            onNewConversationCreated({ id: currectConversationId });
          }
        }, 1500); // Give the backend time to generate the title
      }
    },
    [onResponseComplete, isInApp, onNewConversationCreated]
  );



  const handleDynamicLeadGen = async (userAnswer, conversationId) => {
    const payload = {
      question: leadGenInputs.question || null,
      answer: userAnswer || null,
      input_id: leadGenInputs.input_id,
      stop_after: config?.data_collection?.length,
      conversation_id: conversationId,
      kb_id: config?.kb_id,
    };

    const res = await conversationsService.getDynamicLeadGen(payload);

    if (res.status === 200) {
      if (res.data.finished) {
        setIsLeadGenMode(false);
        setLeadGenCompleted(true);
        leadGenCompletedRef.current = true;
        // Store completion status in localStorage (dynamic based on config)
        setStoredLeadGenStatus(config?.kb_id, { completed: true, declined: false }, config);
        setShowButtons(true);
        setIsAnswerLoading(true);
        return false;
      } else {
        // Find the corresponding field in data_collection_fields
        const currentField = config?.data_collection_fields?.find(
          field => field.id === res.data.next_question_input_id
        );

        handleMessagesOnChat('add', {
          id: uuidv4(),
          content: res.data.next_question,
          type: 'message',
          role: 'assistant',
        });
        
        return {
          ...res.data,
          currentField // Include the field information
        };
      }
    }
    return null;
  };



  const evaluateRules = async (message, conversationId, type) => {
    try {
      const response = await showDynamicButtons(
        message,
        conversationId,
        urlParams.get('token')
        // {
        //   show_live_agent: connectedToLiveAgent,
        // }
      );
      const data = response.data;

      if (type === 'data_collection') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_data_form: config?.data_collection && data.show_data_form,
        }));
      }

      if (type === 'live_agent') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_live_agent: (config?.talk_to_live_agent && data.show_live_agent),
        }));
      }

      if (type === 'calendly') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_calendly_url:
            config?.calendly_integration_enabled && data.show_calendly_url,
        }));
      }

      if (data.show_data_form && !isLeadGenMode && !isInApp && !pollResponse && !leadGenDeclined && !leadGenCompleted && !leadGenCompletedRef.current) {
        setIsLeadGenMode(true);
        console.log('here')
        if (firstQuestion && config?.is_data_collection_optional) {
          setShowConsent(true);
          handleMessagesOnChat('add', {
            id: uuidv4(),
            content:
              config?.data_collection_consent_text ||
              DEFAULT_CHAT_MESSAGES.CONSENT_MESSAGE,
            type: 'message',
            role: 'assistant',
          });
          setSavedQuestion({
            id: uuidv4(),
            content: message,
            type: 'message',
            role: 'user',
          });
          setFirstQuestion(false);
          return true;
        }
        const res = await handleDynamicLeadGen(
          message,
          conversationId
        );
        console.log('res', res);
        if (res) {
          setLeadGenInputs({
            input_id: res.next_question_input_id,
            question: res.next_question,
            input_type: res.currentField?.input_type,
            input_options: res.currentField?.input_options,
            input_label: res.currentField?.input_label,
            show_options: true,
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error evaluating rules:', error);
    }
  };

  const pollAgentTakeover = async (hh_conversation_id) => {
    
    if (!hh_conversation_id) {
      console.error('Invalid conversation ID for WebSocket connection');
      return;
    }

    // Skip if already connected to this conversation
      if (activeConnectionIdRef.current === hh_conversation_id && isWebSocketConnected) {
        return;
      }

    try {
      setPollResponse(true);

      // Reset file upload settings for new human handover conversation
      setFileUploadSetting(null);
      setExplicitlyEnabledFileUpload(false);

      setInteractingWithLiveAgent(false);
      setIsAnswerLoading(false);

      // Disconnect any existing connection first
      disconnectWebSocket();
      
      // Add a small delay to ensure disconnect completes before connecting
      await new Promise(resolve => setTimeout(resolve, 300));

      // Connect to WebSocket
      // This will use the onMessageReceived, onAgentJoined, and onAgentResolved handlers
      // that are already defined in the internalWebSocketHook
      connectWebSocket(hh_conversation_id);
      
      // Update active connection ID
      activeConnectionIdRef.current = hh_conversation_id;
    } catch (e) {
      console.error('Error in pollAgentTakeover:', e);
    }
  };

  //TODO: Refactor this in the future
  const connectToLiveAgent = async (userName, userEmail, conversation) => {
    try {
      const formInputs = [
        {
          input_name: 'name',
          input_type: 'text',
          input_label: 'name',
          input_placeholder: 'name',
          is_mandatory: true,
          input_value: userName,
        },
        {
          input_name: 'email',
          input_type: 'email',
          input_label: 'email',
          input_placeholder: 'email',
          is_mandatory: true,
          input_value: userEmail,
        },
      ];
      await createSharedFormData(
        conversation.id,
        urlParams.get('token'),
        formInputs
      );
      const connectingMessageId = uuidv4();
      const infoTypeMessage = {
        id: connectingMessageId,
        content: 'Connecting you with a team member',
        type: 'info',
        isConnecting: true, // Flag to identify this as the connecting message
      };
      handleMessagesOnChat('add', infoTypeMessage);
      const response = await requestTakeover(
        conversation.id,
        urlParams.get('token'),
        {
          user_email: userEmail,
          user_name: userName,
          parent_url: parentUrl
        },
      );

      await pollAgentTakeover(response.data.id);
    } catch (error) {
      console.error('Error connecting to live agent:', error);
    }
  };

  /**
   * Sends a question to the server and manages the chat state during the process.
   * @param {Object} question - The question object containing content and metadata.
   * @param {Array} imageUrls - Optional array of image URLs to include with the message.
   */
  const sendQuestion = useCallback(
    async (tempQuestion, imageUrls = []) => {
      setIsAnswerLoading(true);
      let isNewConversation = false;
      let sourcesFetching = false;
      const controller = new AbortController();
      setControl(controller);

      let tempAnswer = '';
      let question = tempQuestion;
      let tempAnswerId = uuidv4();

      let conversation = isInHumanHandoverApp ? liveAgentConversation : currentConversation;

      if (!conversation?.id) {
        const newConversation = await createNewConversation();
        if (newConversation) {
          conversation = newConversation;
          isNewConversation = true;
        }
      }

      // Check for live agent interaction FIRST before checking lead gen mode
      // This ensures live agent flow takes precedence over lead generation
      if (interactingWithLiveAgent && (!urlParams.get('client_full_name') || !urlParams.get('client_email'))) {
        setShowButtons(false);

        if (!userName && hasAskedForName) {
          // User has provided their name in response to our request
          setUserName(question.content);
          setIsAnswerLoading(false);
          // Ask for the email
          const liveAgentEmailRequestMessage = {
            id: uuidv4(),
            content: `Thank you, ${question.content}. ${
              config?.live_agent_email_prompt ||
              'Now, could you please provide your email?'
            }`,
            type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
            role: 'assistant',
            date_created: new Date(),
          };
          handleMessagesOnChat('add', liveAgentEmailRequestMessage);
        } else if (userName && !userEmail) {
          // User has provided their email in response to our request
          setUserEmail(question.content);
          connectToLiveAgent(userName, question.content, conversation);
        } else if (!hasAskedForName) {
          // This is the first message after live agent was triggered, ask for name first
          setHasAskedForName(true);
          setIsAnswerLoading(false);
          const liveAgentNameRequestMessage = {
            id: uuidv4(),
            content: config?.live_agent_name_prompt || 'Before we connect you to with a member of our team, could you please tell me your name?',
            type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
            role: 'assistant',
            date_created: new Date(),
          };
          handleMessagesOnChat('add', liveAgentNameRequestMessage);
        } else {
          // Both name and email are collected, connect to live agent
          connectToLiveAgent(userName, userEmail, conversation);
        }
        return;
      }
      
      // Only check lead gen mode if not interacting with live agent
      if (isLeadGenMode && !isInApp && !pollResponse) {
        setHideFooter(true);
        setShowButtons(false);
        setIsAnswerLoading(false);

        if (tempQuestion.content.toLowerCase() === 'skip to general chat') {
          setIsLeadGenMode(false);
          setLeadGenDeclined(true); // Mark that user declined lead gen
          // Store declined status in localStorage (dynamic based on config)
          setStoredLeadGenStatus(config?.kb_id, { completed: false, declined: true }, config);
          setShowButtons(true);
          question = savedQuestion;
          setIsAnswerLoading(true);
        } else {
          const res = await handleDynamicLeadGen(
            question.content,
            conversation.id
          );

          if (res) {
            setLeadGenInputs({
              input_id: res.next_question_input_id,
              question: res.next_question,
              input_type: res.currentField?.input_type,
              input_options: res.currentField?.input_options,
              input_label: res.currentField?.input_label,
              show_options: true,
            });
            return;
          } else {
            setIsLeadGenMode(false);
            setLeadGenCompleted(true);
            leadGenCompletedRef.current = true;
            // Store completion status in localStorage (dynamic based on config)
            setStoredLeadGenStatus(config?.kb_id, { completed: true, declined: false }, config);
            setShowButtons(true);
            question = savedQuestion;
            setIsAnswerLoading(true);
          }
        }
      }

      if(config.data_collection && !config?.show_data_collection_on_conversation_begin && !isInApp && !isPreviewMode && !leadGenDeclined && !leadGenCompleted && !leadGenCompletedRef.current) {
        const res = await evaluateRules(question.content, conversation.id, 'data_collection');
        if (res) {
          if(firstQuestion) {
            setSavedQuestion(question);
            setFirstQuestion(false);
          }
          setIsAnswerLoading(false);
          return;
        }
        setIsAnswerLoading(true);
      }

      if (
        config?.talk_to_live_agent &&
        !config?.show_live_agent_on_conversation_begin &&
        (isAgentAvailable || config.show_live_agent_rule !== '') &&
        !isInApp &&
        !isPreviewMode
      ) {
        const res = await evaluateRules(
          question.content,
          conversation.id,
          'live_agent'
        );
        if (res) {
          return;
        }
      }

      if(config?.calendly_integration_enabled && !config?.show_calendly_at_the_start && !isInApp && !isPreviewMode) {
        const res = await evaluateRules(question.content, conversation.id, 'calendly');
        if (res) {
          return;
        }
      }

      if (pollResponse && !isLiveAgentResolved) {
        setIsAnswerLoading(false);

        // Send message through WebSocket
        sendWebSocketMessage(
          question.content,
          questionIsQuickResponse,
          imageUrls || []
        );

        setQuestionIsQuickResponse(false);
        return;
      }

      setHideFooter(false);
      const messageTimestamp = new Date();
      handleMessagesOnChat('add', {
        id_temp: tempAnswerId,
        content: '',
        type: 'normal',
        role: 'assistant',
        status: 'loading',
        reaction: '',
        date_created: messageTimestamp,
      });

      const handleError = (err) => {
        // notify(
        //   'failure',
        //   `${
        //     embedMode
        //       ? 'We could not process your request. Please try again and if the issue persists, contact support for help.'
        //       : 'We could not process your request. Please try again and if the issue persists, contact us on support@dante-ai.com for help.'
        //   }`
        // );
        if (err instanceof FatalError) {
          throw err;
        } else {
          controller.abort();
          setIsAnswerLoading(false);
          throw new RetriableError();
        }
      };

      const handleMessage = (event) => {
        queryStreamOpen.current = true;

        if (event.event === 'error') {
          // handle error here...
        }

        if (event.event === 'ping' || event.event === 'talk') return;

        if (event.event === 'message_id') {
          // Batch the updates using React's automatic batching
          React.startTransition(() => {
            handleMessagesOnChat('update', {
              id: event.data,
              id_temp: tempAnswerId,
              date_created: new Date(), // Add timestamp when message ID is received
            });

            // Use functional update to ensure we have the latest state
            setShowSources(prevSources => {
              const newSources = [...prevSources, event.data];
              return newSources;
            });
          });

          return;
        }

        if (event.data === '_LOADING_IMAGE_START_ ') {
          setChatImageLoader(true);
        } else if (event.data === '_LOADING_IMAGE_END_') {
          setChatImageLoader(false);
        } else if (event.data === '_STREAMING_FINISHED_') {
          setSourcesReady(true);
          controller.abort();
          const answer = {
            id_temp: tempAnswerId,
            content: tempAnswer,
            status: 'complete',
            role: 'assistant',
            reaction: '',
            date_created: messageTimestamp, // Preserve the original timestamp
          };
          handleMessagesOnChat('update', answer);
          finishGeneratingAnswer(conversation.id, isNewConversation);
          setMessageLoaded(true);
        } else if (event.data === '_STARTING_SOURCES_FETCHING_') {
          // Sources fetching started
        } else if (!sourcesFetching) {
          const parsedData = formatEventData(event.data);
          tempAnswer += parsedData;
          handleMessagesOnChat('update', {
            id_temp: tempAnswerId,
            content: tempAnswer,
            status: 'typing',
            role: 'assistant',
            reaction: '',
            date_created: new Date(), // Add timestamp for each content update
          });
        }
      };

      const handleOpenStream = (res) => {
        if (res.status === 426) {
          setSaveSignUpBtn(true);
          window.parent.postMessage(
            {
              eventType: 'signup-btn',
              eventData: true,
            },
            '*'
          );
        }
        if (res.ok && res.status === 200) {
        } else if (
          res.status >= 400 &&
          res.status < 500 &&
          res.status !== 429
        ) {
          if (res.status === 422) {
            // notify('failure', 'Could not process your request.');
          }
          if (res.status === 402) {
            // notify('failure', 'Monthly credit limit exceeded. Top up using credits top-up button.');
          } else if (res.status === 403) {
            // notify('failure', 'Upgrade your plan to use GPT-4.');
          } else if (res.status === 400) {
            // notify('failure', 'Retrain your untrained chatbot.');
          }
        }
      };
      const handleCloseStream = () => {
        control.abort();
      };

      const messageHandlers = {
        onMessage: handleMessage,
        onOpen: handleOpenStream,
        onClose: handleCloseStream,
        onError: handleError,
      };

      try {
        setMessageLoaded(false);
        setOnGoingStream(true);
        // Use selectedChatbot.llmModel first, then fall back to last_model_used, then fall back to default
        const llmModel = selectedChatbot?.llmModel?.value || selectedChatbot?.last_model_used?.value || urlParams.get('modeltype') || LLM_MODEL_DEFAULT.value;
        const conversationPayload = {
          ...conversation,
          llmModel,
          question: question.content,
          messages: messages.slice(-5),
        };

        if (isInApp && !isPreviewMode) {
          await sendMessageInApp({
            conversation: conversationPayload,
            accessToken: config?.access_token,
            ...messageHandlers,
            control: controller,
          });
          const creditsResponse = await userService.getUserCredits();
          if (creditsResponse.status === 200) {
            setUserData({
              ...userData,
              credits_key: {
                ...creditsResponse.data,
              },
            });
          }
        } else if (!isInApp && !isPreviewMode) {
          await sendMessageEmbed({
            conversation: conversationPayload,
            accessToken: config?.token,
            ...messageHandlers,
            control: controller,
            cookie: cookie,
            userID: getSessionUserId(config?.kb_id),
          });
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setOnGoingStream(false);
      }
    },
    [
      currentConversation,
      isInApp,
      config,
      messages,
      setCurrentConversation,
      interactingWithLiveAgent,
      isLeadGenMode,
      questionIsQuickResponse,
      savedQuestion,
      cookie,
    ]
  );
  /**
   * Handles adding or updating messages in the chat.
   *
   * @param {string} action - The action to perform ('add' or 'update').
   * @param {Object} message - The message object to add or update.
   */
  const handleMessagesOnChat = useCallback(
    (action, message) => {
      
      if (action === 'add') {
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages, message];
          return updatedMessages;
        });
      }
      if (action === 'update') {
        setMessages((prevMessages) => {
          let hasChanges = false;
          const updatedMessages = prevMessages.map((msg) => {
            if (msg.id && msg.id === message.id) {
              // Check if the message actually changed
              const hasActualChanges = Object.keys(message).some(key => {
                // if (key === 'id' || key === 'id_temp') return false; // Skip ID fields
                return msg[key] !== message[key];
              });

              if (hasActualChanges) {
                hasChanges = true;
                return { ...msg, ...message };
              } else {
                return msg;
              }
            }
            if (msg.id_temp && msg.id_temp === message.id_temp) {
              // Check if the message actually changed
              const hasActualChanges = Object.keys(message).some(key => {
                // if (key === 'id' || key === 'id_temp') return false; // Skip ID fields
                return msg[key] !== message[key];
              });

              if (hasActualChanges) {
                hasChanges = true;
                return { ...msg, ...message };
              } else {
                return msg;
              }
            }
            return msg;
          });

          if (hasChanges) {
            return updatedMessages;
          } else {
            return prevMessages; // Return the same reference to prevent re-render
          }
        });
      }

    },
    [messages]
  );

  // Expose handleMessagesOnChat function through ref
  useImperativeHandle(ref, () => ({
    handleMessagesOnChat
  }), [handleMessagesOnChat]);

  /**
   * Handles sending a new question.
   *
   * @param {string} question - The question to send to the assistant.
   */
  const handleSendQuestion = useCallback(
    async (question, imageUrls = []) => {

      if(customizationData?.blocked_words?.length > 0) {
       
        const foundBlockedWords = customizationData.blocked_words.filter(word => {
        
          const wordPattern = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          return wordPattern.test(question);
        });

        if (foundBlockedWords.length > 0) {
          // Show warning notification with all blocked words found
          const message = foundBlockedWords.length === 1
            ? `Your message contains a blocked word: "${foundBlockedWords[0]}". Please remove it and try again.`
            : `Your message contains blocked words: ${foundBlockedWords.map(word => `"${word}"`).join(', ')}. Please remove them and try again.`;

          handleNotification({
            type: 'alert',
            message,
            duration: 5000,
          });

          return;
        }
      }
      setHasStartedConversation(true);
      
      // Hide select options when a message is sent during lead generation
      if (isLeadGenMode && leadGenInputs?.input_type === 'select') {
        setLeadGenInputs(prev => ({
          ...prev,
          show_options: false
        }));
      }
      
      // Use the hook's markAsShown instead of the old logic
      markAsShown();

      const questionId = uuidv4();
      const dateCreated = new Date();
      const newQuestion = {
        id: questionId,
        content: question,
        role: isInHumanHandoverApp ? 'assistant' : 'user',
        date_created: dateCreated,
        images: imageUrls,
        type:
          isInHumanHandoverApp && questionIsQuickResponse
            ? 'quick_response'
            : 'message',
        // Mark if message was sent during live agent interaction
        sentDuringLiveAgent: pollResponse || interactingWithLiveAgent,
      };
      if(isInHumanHandoverApp) {
        console.log('liveAgentData', liveAgentData)
        newQuestion.agent_name = liveAgentData?.name;
        newQuestion.agent_title = liveAgentData?.agent_title;
        newQuestion.agent_profile_pic = liveAgentData?.image;
      }
      handleMessagesOnChat('add', newQuestion);
      
      // Check if we're in live agent mode first - this takes precedence over lead gen
      if (interactingWithLiveAgent) {
        // Skip lead gen flow if we're interacting with a live agent
        await sendQuestion(newQuestion, imageUrls);
        return;
      }
      

      
      // Only proceed with lead gen if not interacting with live agent
      if (
        isLeadGenMode &&
        firstQuestion &&
        !isInApp &&
        !isInHumanHandoverApp &&
        !pollResponse
      ) {
        setIsAnswerLoading(false);
        setIsLeadGenMode(true);
        setSavedQuestion(newQuestion);
        setFirstQuestion(false);
        setShowButtons(false);
        if(config?.is_data_collection_optional){
          setHideFooter(true);
          setShowConsent(true);
          handleMessagesOnChat('add', {
            id: uuidv4(),
            content:
              config?.data_collection_consent_text ||
              DEFAULT_CHAT_MESSAGES.CONSENT_MESSAGE,
            type: 'message',
            role: 'assistant',
          });
          return;
        }
      }
      await sendQuestion(newQuestion, imageUrls);
    },
    [
      markAsShown, 
      isInHumanHandoverApp, 
      questionIsQuickResponse, 
      liveAgentData, 
      interactingWithLiveAgent, 
      isLeadGenMode, 
      firstQuestion, 
      isInApp, 
      pollResponse, 
      config?.is_data_collection_optional, 
      config?.data_collection_consent_text, 
      sendQuestion
    ]
  );

  const handleNotification = ({ type, message, duration }) => {
    setNotification({
      show: true,
      type,
      message,
    });

    if (duration) {
      setTimeout(() => {
        setNotification({
          show: false,
        });
      }, duration);
    }
  };

  const resetConversation = useCallback(() => {
    setMessages(
      config?.initial_messages || customizationData?.initial_messages || []
    );

    // Use the hook's resetPrompts instead of the old logic
    resetPrompts();
    setHasStartedConversation(false);
    setShowConsent(false);
    setPlayed([]);

    // Reset live agent states
    setInteractingWithLiveAgent(false);
    setUserName('');
    setUserEmail('');
    setHasAskedForName(false);



    // Reset the message loading flag
    hasLoadedMessages.current = false;

    // Disconnect the WebSocket
    disconnectWebSocket();
  }, [resetPrompts, disconnectWebSocket]);

  const handleCloseChat = () => {
    // Disconnect the WebSocket before clearing messages
    disconnectWebSocket();
    
    // Reset connection tracking
    activeConnectionIdRef.current = null;
    
    setMessages([]);
    setShouldFetchCustomization(true);
    hasLoadedMessages.current = false;
    
    // Call the parent's close button handler if provided
    handleCloseButton && handleCloseButton();
  };
  /**
   * Loads and processes messages for a specific conversation by its ID.
   * Supports both standard and live agent conversations.
   *
   * @param {string} conversation_id - The ID of the conversation to load messages from.
   * @param {boolean} isLiveAgent - Determines whether to fetch messages from live agent service.
   * @returns {Promise<void>} A promise that resolves when the messages are loaded and processed.
   */
  const loadConversationMessages = async (
    conversation_id,
    isLiveAgent = false
  ) => {
    try {
      
      // Set loading state to true
      setMessagesLoading(true);
      // Fetch the actual conversation messages
      let conversation;
      if (isLiveAgent) {
        const response = await humanHandoverService.getMessagesByConversationId(
          conversation_id,
          timezone
        );
        conversation = response.data;
      } else if (isInApp) {
        const response = await messagesService.getMessagesByConversationId(
          conversation_id,
          timezone
        );
        conversation = response.data;
      } else if (!isInApp && !isPreviewMode && !isInHumanHandoverApp) {
        const response = await messagesService.getSharedMessagesByConversationId(
          conversation_id,
          config?.token
        );
        conversation = response.data;
      }

      if(!isLiveAgent && !isInApp) {
        const { data: conversationMode } = await humanHandoverService.getConversationMode(conversation_id, config?.token);
        
        if (conversationMode.conversation_mode === 'human_handover') {
          setIsPreviousConversationHasLiveAgent(true);
          
          // Check if conversation is resolved before reconnecting
          if (conversationMode.state !== 'resolved') {
            pollAgentTakeover(conversationMode.hh_conversation_id);
            setIsHumanHandover && setIsHumanHandover(true);
            setIsLiveAgentResolved(false); // Ensure resolved state is properly set
          } else {
            setIsLiveAgentResolved(true);
          }
          
          setAgentName(conversationMode.agent_name);
          setAgentTitle(conversationMode.agent_title);
          setAgentAvatar(conversationMode.agent_image);
          setAgentJoined(conversationMode.agent_joined);
        }
      }

      // Choose the appropriate transformation function
      const transformFn = isLiveAgent
        ? transformMessageToBubbleMessageHumanHandover
        : transformMessageToBubbleMessage;

      // Transform the conversation results
      const conversationMessages = conversation.results.flatMap(transformFn);

      console.log('Conversation messages', conversationMessages);
      // Set the new conversation messages while preserving initial messages
      const initialMessages = config?.initial_messages || customizationData?.initial_messages || [];
      const allMessages = [...initialMessages, ...conversationMessages];
      console.log('🎯 All messages:', allMessages);
      setMessages(allMessages);

      if (!isLiveAgent) {
        setIsLeadGenMode(false);
        // Don't reset lead gen declined flag - preserve localStorage state
        // setLeadGenDeclined(false); // Removed to maintain persistence
        setShowConsent(false);
        setShowSources(conversation.results?.map((result) => result.id) || []);
      }

      // Set loading state to false after messages are loaded
      setTimeout(() => {
        setMessagesLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      setMessagesLoading(false);
    }
  };

  // Handle Live Agent
  const handleOpenLiveAgent = async () => {
    markAsShown();

    setShowButtons(false);
    setInteractingWithLiveAgent(true);
    setIsAnswerLoading(false);

    // Explicitly disable lead generation mode when switching to live agent
    setIsLeadGenMode(false);
    // Don't reset lead gen declined flag - preserve localStorage state when switching to live agent
    // setLeadGenDeclined(false); // Removed to maintain persistence
    setShowConsent(false);
    setHideFooter(false);

    //if there is no conversation id create one
    let conversation = currentConversation;
    console.log('conversation', currentConversation);
    if (!currentConversation?.id) {
      conversation = await createNewConversation();
    }

    if (!urlParams.get('client_full_name') || !urlParams.get('client_email')) {
      setHasAskedForName(true);
      const liveAgentNameRequestMessage = {
        id: uuidv4(),
        content: config?.live_agent_name_prompt || 'Before we connect you to with a member of our team, could you please tell me your name?',
        type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
        role: 'assistant',
        reaction: '',
        date_created: new Date(),
      };
      handleMessagesOnChat('add', liveAgentNameRequestMessage);
    } else {
      connectToLiveAgent(urlParams.get('client_full_name'), urlParams.get('client_email'), conversation);
    }
  };

  const handleUpdateReaction = async ({ message_id, action }) => {
    try {
      if (!message_id) {
        throw new Error('Invalid message_id for updating reaction');
      }

      await messagesService.updateReaction(message_id, action);
      

      const message = messages.find((msg) => msg.id === message_id || msg.id_temp === message_id);

      if (action && action !== 'no_reaction') {
        handleNotification({
          type: action === 'thumbs_up' ? 'positive' : 'negative',
          message:
            action === 'thumbs_up'
              ? 'Response rated as helpful'
              : 'Response rated as not helpful',
          duration: 3000,
        });
      } else {
        handleNotification({
          type: 'neutral',
          message: 'Reaction removed',
          duration: 3000,
        });
      }

      handleMessagesOnChat('update', {
        ...message,
        reaction: action,
      });
    } catch (error) {
      console.error(
        `Error updating reaction for message_id=${message_id}, action=${action}:`,
        error
      );
    }
  };

  const ttsPlayHandler = async (index, playAudioFn, token = null) => {
    if (played.includes(index)) return;

    try {
      const { data } = token ? await playAudioFn(token) : await playAudioFn();
      if (!data) {
        setPlayed((prev) => [...prev, index]);
      }
    } catch (error) {
      console.error('Error playing audio TTS:', error);
    }
  };

  const ttsPlay = (index) => ttsPlayHandler(index, ttsService.playAudioTTS);

  const ttsPlayShared = (index, token) =>
    ttsPlayHandler(index, ttsService.playSharedAudioTTS, token);

  const handleSpeech = (answer, index, message) => {
    const synth = window.speechSynthesis;

    if (speechPlaying && speechPlayingID !== index) {
      synth.cancel();
    }

    if (!speechPlaying || (speechPlaying && speechPlayingID !== index)) {
      const voice = voices.find(
        (v) =>
          (v.lang === 'en-US' || v.lang === 'en-GB') &&
          v.name === 'Google UK English Female'
      );
      const speed = 1.05;
      const msg = new SpeechSynthesisUtterance(answer);
      msg.voice = voice || null;
      msg.rate = speed;

      const buffer = setInterval(() => {
        synth.pause();
        synth.resume();
      }, 5000);

      setSpeechPlaying(true);
      setSpeechPlayingID(index);
      let speakDuration;
      msg.onstart = () => {
        setNotification({
          show: true,
          type: 'alert',
          message: 'Reading message',
        });
      };
      msg.onend = (event) => {
        setSpeechPlaying(false);
        setSpeechPlayingID(null);
        clearInterval(buffer);
        setNotification({
          show: false,
        });
        speakDuration = event.elapsedTime / speed;
      };

      synth.speak(msg);

      if (!isInApp) {
        ttsPlayShared(index, config?.token);
      } else {
        ttsPlay(index);
      }
    } else {
      setSpeechPlaying(false);
      setSpeechPlayingID(null);
      synth.cancel();
      setNotification({
        show: false,
      });
      handleMessagesOnChat('update', {
        ...message,
        reaction: 'stop_message',
      });
    }
  };

  const handlePlayMessage = ({ message_id }) => {
    if (!message_id) {
      console.error('Missing message_id for playing message');
      return;
    }

    const message = messages.find((msg) => msg.id === message_id || msg.id_temp === message_id);

    if (message) {
      handleMessagesOnChat('update', {
        ...message,
        reaction: 'play_message',
      });
      handleSpeech(message.content, message.id || message.id_temp, message);
    } else {
      console.error('Message not found for TTS:', message_id);
    }
  };

  const handleSources = async ({ message_id }) => {
    if (!featureCheck('sources')) {
      return;
    }
    setSourcesLoading([...sourcesLoading, message_id]);
    try {
      const response = await modelService.getSources(message_id);

      if (response.status === 200) {
        // Set sources even if null or empty, modal will show "No sources found"
        setSources(response.data || []);
        setOpenSources(true);
        setSourcesLoading([]);
      } else {
        // If response status is not 200, still open modal with empty sources
        setSources([]);
        setOpenSources(true);
        setSourcesLoading([]);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      // On error, still open modal to show "No sources found"
      setSources([]);
      setOpenSources(true);
      setSourcesLoading([]);
    }
  };

  const handleFooterButton = ({ message_id, action }) => {
    if (!message_id) {
      console.error('Invalid parameters for footer button action');
      return;
    }

    const actionHandlers = {
      thumbs_up: () => handleUpdateReaction({ message_id, action }),
      thumbs_down: () => handleUpdateReaction({ message_id, action }),
      play_message: () => handlePlayMessage({ message_id }),
      stop_message: () => handlePlayMessage({ message_id }),
      sources: () => handleSources({ message_id }),
      no_reaction: () =>
        handleUpdateReaction({ message_id, action: 'no_reaction' }),
    };

    const handler = actionHandlers[action];
    if (handler) {
      handler();
    } else {
      console.warn(`Unhandled action: ${action}`);
    }
  };

  useEffect(() => {
    // Only fetch data if the message is loaded and it's not in app
    // is necessary to get the id of the message to update the reaction
    if ( messageLoaded) {
      const fetchData = async () => {
        try {
           const responseMessages = isInApp ? await messagesService.getMessagesByConversationId(
            currentConversation.id,
          ) : await messagesService.getSharedMessagesByConversationId(
              currentConversation.id,
              config?.token
            );

          if (responseMessages?.data) {
            const data = responseMessages.data;
            const results = data.results;
            // if the conversation is not in the store, add it
            // if the conversation is in the store, update it with the last message
            addConversation(config?.kb_id, {
              id: currentConversation.id,
              dateCreated: currentConversation.date_created,
              lastMessage: results[results.length - 1].answer,
              interfaceType: interfaceType,
            });
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchData();
    }
  }, [messageLoaded]);

  useEffect(() => {
    if (shouldFetchCustomization && config?.kb_id && !isPreviewMode && (!customizationData?.id && isInHumanHandoverApp)) {
      // Call the API only when shouldFetch is true
      const fetchCustomization = async () => {
        try {
          const customizationFetchFn = isInApp
            ? customizationService.getChatbotCustomizationById
            : customizationService.getSharedChatbotCustomizationById;

          const { data: customizationResponse } = await customizationFetchFn({
            kb_id: config?.kb_id,
            ...(!isInApp && { token: config?.token }),
          });
          
          setCustomizationData(customizationResponse);
          
          setShouldFetchCustomization(false);
        } catch (e) {
          console.error('Error fetching customization:', e);
        }
      };
      fetchCustomization();
    }
  }, [
    shouldFetchCustomization,
    isInApp,
    config?.id,
    config?.access_token,
    isInHumanHandoverApp
  ]);

  useEffect(() => {
    // Generate a unique key for this chatbot and interface combination
    const sessionKey = `dante-conversation-${config?.kb_id}-${interfaceType}`;
    
    // Check if this is a fresh page load/re-embed vs tab switch
    const isFreshLoad = () => {
      // Use a more specific session storage key that includes chatbot ID and interface type
      const lastAccessTime = sessionStorage.getItem(sessionKey);
      const currentTime = Date.now();
      
      // If there's no last access time or it was more than 30 minutes ago, consider it a fresh load
      if (!lastAccessTime || (currentTime - parseInt(lastAccessTime, 10)) > 30 * 60 * 1000) {
        sessionStorage.setItem(sessionKey, currentTime.toString());
        return true;
      }
      
      // Update the last access time
      sessionStorage.setItem(sessionKey, currentTime.toString());
      return false;
    };

    // Only reset conversation on fresh load, not on tab switches,
    // and only if we're not explicitly opening a conversation
    if (isFreshLoad() && currentConversation.type !== 'opening') {
      resetCurrentConversation();
    }

    // Handle page visibility changes (when user switches tabs and comes back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInApp && !isPreviewMode) {
        // Check if we need to reconnect
        if (currentConversation?.id && 
            isPreviousConversationHasLiveAgent && 
            !isWebSocketConnected && 
            !isLiveAgentResolved) {
          // Only reconnect if we have a conversation ID and it's not resolved
          humanHandoverService.getConversationMode(currentConversation.id, config?.token)
            .then(response => {
              const conversationMode = response.data;
              if (conversationMode.conversation_mode === 'human_handover' && 
                  conversationMode.state !== 'resolved') {
                pollAgentTakeover(conversationMode.hh_conversation_id);
              }
            })
            .catch(error => {
              console.error('Error checking conversation mode:', error);
            });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [config?.kb_id, interfaceType, resetCurrentConversation, currentConversation.type]);

  useEffect(() => {
    if (
      currentConversation.id === null &&
      currentConversation.type === null &&
      customizationData &&
      !isPreviewMode &&
      !isInHumanHandoverApp
    ) {
      resetConversation();
    } else if (
      currentConversation.id !== null &&
      currentConversation.type === 'opening' && !isInHumanHandoverApp
    ) {
      const currentConversationId = currentConversation.id;
      const prevConversationId = prevConversationIdRef.current;
      
      // Only clear messages if we're switching to a different conversation
      if (prevConversationId !== currentConversationId) {
        const initialMessages = config?.initial_messages || customizationData?.initial_messages || [];
        console.log('setting messages', [...initialMessages]);
        setMessages([...initialMessages]);
      }
      console.log('Loading conversation messages 2' );
      loadConversationMessages(currentConversation.id);
      prevConversationIdRef.current = currentConversationId;
    } else if (
      currentConversation.id !== null &&
      currentConversation.type === 'new'
    ) {
      // Don't load messages for 'new' conversations - they will be handled by initial messages flow
      // Keep the type as 'new' to prevent iframe restoration
    }
  }, [currentConversation]);

  useEffect(() => {
    // Early return if not in human handover app
    if (!isInHumanHandoverApp) {
      return;
    }
    
    
    if (
      liveAgentConversation &&
      customizationData &&
      (config?.initial_messages || customizationData?.initial_messages) &&
      !hasLoadedMessages.current
    ) {
      hasLoadedMessages.current = true;

      // Set loading state to true before starting
      setMessagesLoading(true);

      // Then load the conversation messages
      if (liveAgentConversation?.id) {
        // If we have an active connection to a different conversation, disconnect it first
        if (activeConnectionIdRef.current && activeConnectionIdRef.current !== liveAgentConversation.id) {
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
        
        loadConversationMessages(liveAgentConversation.id, true);

        // Reset file upload settings when conversation is opened
        // The actual settings will be fetched when agent joins via humanHandoverTakeConversation
        if (isInHumanHandoverApp) {
          setExplicitlyEnabledFileUpload(false);
          setFileUploadSetting(null);
        }

        // Only connect if the conversation is not resolved
        if (liveAgentConversation?.state !== 'resolved') {
          connectWebSocket(liveAgentConversation.id);
          setIsLiveAgentResolved(false);
        } else {
          setIsLiveAgentResolved(true);
          // Make sure WebSocket is disconnected for resolved conversations
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
      }
    }
  }, [liveAgentConversation, customizationData, isInHumanHandoverApp]);

  useEffect(() => {
    setCustomizationData(config);
    setDynamicButtons((prev) => ({
      ...prev,
      show_calendly_url:
        config?.show_calendly_at_the_start &&
        config?.calendly_integration_enabled,
      show_data_form:
        config?.show_data_collection_on_conversation_begin &&
        config?.data_collection,
      show_live_agent:
        config?.talk_to_live_agent && (config?.show_live_agent_on_conversation_begin && isAgentAvailable),
    }));
    // Update lead gen mode based on config and stored status (dynamic based on config)
    if (config?.kb_id) {
      const storedStatus = getStoredLeadGenStatus(config.kb_id, config);
      setLeadGenCompleted(storedStatus.completed);
      setLeadGenDeclined(storedStatus.declined);
      leadGenCompletedRef.current = storedStatus.completed;

      // Only enable lead gen mode if not completed/declined and config allows it
      setIsLeadGenMode(
        config?.data_collection && !storedStatus.completed && !storedStatus.declined
          ? config?.show_data_collection_on_conversation_begin
          : false
      );
    } else {
      setIsLeadGenMode(
        config?.data_collection
          ? config?.show_data_collection_on_conversation_begin
          : false
      );
    }
    setHasMic(config?.has_mic);
    
    if (config?.notification) {
      setNotification(config?.notification);
    }
  }, [config, isAgentAvailable]);

  useEffect(() => {
    
    // Don't overwrite messages if we're in the middle of a conversation or loading
    // BUT allow updates in preview mode for real-time preview of initial messages
    if ((currentConversation?.type === 'opening' || messages.length > 1) && !isPreviewMode) {
      return;
    }
    if (isInHumanHandoverApp) {
      const newMessages = [...(config?.initial_messages || []), ...(config?.messages || []), ...messages];
      setMessages(newMessages);
    } else {
      const newMessages = [...(config?.initial_messages || []), ...(config?.messages || [])];
      setMessages(newMessages);
    }
  }, [config?.initial_messages, config?.messages, currentConversation?.type, isInHumanHandoverApp, messages.length, isPreviewMode]);

  // Keep track of the last processed conversation ID to prevent duplicate processing
  const lastProcessedConversationRef = useRef(null);

  useEffect(() => {
    if (liveAgentConversation) {
      // Skip if we've already processed this exact conversation update
      if (lastProcessedConversationRef.current === liveAgentConversation.id) {
        return;
      }
      
      // Update the last processed conversation ID
      lastProcessedConversationRef.current = liveAgentConversation.id;
      
      if (
        liveAgentConversation?.state !== 'waiting' &&
        liveAgentConversation?.state !== 'ai' &&
        liveAgentConversation?.state !== 'resolved' &&
        activeConnectionIdRef.current !== liveAgentConversation.id
      ) {
        pollAgentTakeover(liveAgentConversation.id);
      } else {
        if (liveAgentConversation?.state === 'resolved') {
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
      }
    }
  }, [liveAgentConversation, isWebSocketConnected]);

  useEffect(() => {
    if (promptToSend) {
      const currentPrompt = promptToSend;
      setPromptToSend(null);
      
      setTimeout(() => {
        markAsShown();
        handleSendQuestion(currentPrompt)
          .then(() => {
            onPromptProcessed();
          })
          .catch((error) => {
            console.error('Error sending prompt:', error);
          });
      }, 100);
    }
  }, [promptToSend, onPromptProcessed, markAsShown]);

  useEffect(() => {
    setIsLiveAgentResolved(isLiveAgentConversationResolved);
  }, [isLiveAgentConversationResolved]);



  const humanHandoverTakeConversation = async () => {
    try {
      if (!liveAgentConversation?.id) {
        console.error('Cannot take conversation: Missing conversation ID');
        return;
      }

      const response = await humanHandoverService.takeConversation(
        liveAgentConversation.id
      );

      if (response.status === 200) {
        addOpenedConversation({
          ...response.data,
          open: true,
        });

        // Fetch file upload settings for this conversation when agent joins
        // This is the correct time to fetch these settings - after the Join button is clicked
        try {
          const fileSettingsResponse = await humanHandoverService.getChatFileSettingsByConversationId(
            liveAgentConversation.id
          );

          // Handle different response structures
          let isEnabled = false;
          if (typeof fileSettingsResponse.data === 'boolean') {
            isEnabled = fileSettingsResponse.data;
          } else if (fileSettingsResponse.data?.allow_file_upload !== undefined) {
            isEnabled = fileSettingsResponse.data.allow_file_upload;
          } else {
            isEnabled = false;
          }

          setFileUploadSetting(isEnabled);
          if (isEnabled) {
            setExplicitlyEnabledFileUpload(true);
          }
        } catch (fileSettingsError) {
          console.error('Error fetching file upload settings:', fileSettingsError);
          // Set to false to show the enable button
          setFileUploadSetting(false);
          setExplicitlyEnabledFileUpload(false);
        }

        // Connect to the WebSocket if not already connected
        // Use pollAgentTakeover to handle the WebSocket connection
        // This will use the existing WebSocket hook with all its handlers
        await pollAgentTakeover(liveAgentConversation.id);

        if (isInHumanHandoverApp) {
          setTimeout(() => {
            setLiveAgentConversationEvent(liveAgentConversation.id, {
              type: 'human_handover_taken',
              conversation_id: liveAgentConversation.id,
            });
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error taking conversation:', error);
      setNotification({
        show: true,
        type: 'error',
        message: 'Failed to take conversation. Please try again.',
      });
    }
  };

  const humanHandoverMarkResolved = async () => {
    try {
      if (!liveAgentConversation?.id) {
        console.error('Cannot resolve conversation: Missing conversation ID');
        return;
      }

      // Check if the current user is assigned to this conversation
      if (liveAgentConversation?.user_id !== userData?.id) {
        console.error('Cannot resolve conversation: User is not assigned to this conversation');
        setNotification({
          show: true,
          type: 'error',
          message: 'You can only mark conversations as resolved if they are assigned to you.',
        });
        return;
      }

      const response = await humanHandoverService.markResolved(
        liveAgentConversation.id
      );

      if (response.status === 200) {
        setIsLiveAgentResolved(true);
        setPollResponse(false);
        setIsHumanHandover && setIsHumanHandover(false);
        addOpenedConversation({
          ...response.data,
          open: false,
        });

        // Send the human_handover_resolved message - only if NOT in the human handover app
        if (isInHumanHandoverApp) {
          sendWebSocketControlMessage('human_handover_resolved', liveAgentConversation.id);
        }

        // Disconnect the WebSocket
        disconnectWebSocket();
      }
    } catch (error) {
      console.error('Error marking conversation as resolved:', error);
      setNotification({
        show: true,
        type: 'error',
        message: 'Failed to mark conversation as resolved. Please try again.',
      });
    }
  };

  // Function to handle showing the voice preview
  const handleShowVoice = async () => {
    // If there's no current conversation, create a new one
    if (!currentConversation?.id ) {
      try {
        const newConversation = await createNewConversation();
        if (!newConversation) {
          console.error('Failed to create a new conversation for voice preview');
          // We could show an error message to the user here
          return;
        }
        // Wait a moment for the conversation to be properly set up
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error creating conversation for voice preview:', error);
        return;
      }
    }

    // Show the voice preview
    setShowVoicePreview(true);
  };

  // Function to handle closing the voice preview
  const handleCloseVoice = () => {
    setShowVoicePreview(false);

    // Add a delay to allow AI Voice messages to be processed in the backend
    // before refreshing the chat history
    if (currentConversation?.id) {
      // Set loading state immediately to show loader during delay
      setMessagesLoading(true);

      setTimeout(() => {
        loadConversationMessages(currentConversation.id);
      }, 1000);
    }
  };

  useEffect(() => {
    // Cleanup function to close WebSocket connection when component unmounts
    return () => {
      // Disconnect the WebSocket
      disconnectWebSocket();
      activeConnectionIdRef.current = null;
    };
  }, [disconnectWebSocket]);



  useEffect(() => {
    if (isInHumanHandoverApp && isWebSocketConnected) {
      const liveAgentConversationEvents = getLiveAgentConversationEvents(liveAgentConversation.id);
      if (liveAgentConversationEvents) {
        liveAgentConversationEvents.forEach(event => {
          sendWebSocketControlMessage(event.type, event.conversation_id);
          deleteLiveAgentConversationEvent(liveAgentConversation.id, event);
        });
      }
    }
  }, [isInHumanHandoverApp, isWebSocketConnected, sendEventConnected]);

  useEffect(() => {
    const unlockAudio = () => {
      unlockAudioContext();
      document.removeEventListener('click', unlockAudio);
    };
    document.addEventListener('click', unlockAudio, { once: true });
  }, []);

  // Add a ref to track if we've already processed this conversation
  // const processedConversationRef = useRef(null);
  
  // useEffect(() => {
  //   // Create a unique key for this conversation state
  //   const conversationKey = `${currentConversation?.id}-${currentConversation?.type}`;
    
  //   // Skip if we've already processed this exact conversation state
  //   if (processedConversationRef.current === conversationKey) {
  //     return;
  //   }
    
  //   if(currentConversation?.type === 'new' && currentConversation?.id) {
  //     // Update the conversation type to "opening" to load messages
  //     processedConversationRef.current = conversationKey;
  //     setCurrentConversation({
  //       ...currentConversation,
  //       type: 'opening'
  //     });
  //   } else if(currentConversation?.type === 'new' && !currentConversation?.id) {
  //     processedConversationRef.current = conversationKey;
  //   }
  // }, [currentConversation, setCurrentConversation, resetCurrentConversation]);
  
  return (
    <>

      
      {/* Voice Preview Modal */}
      {showVoicePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000000] bg-opacity-80">
          <div className="relative w-full h-full sm:max-w-[90%] md:max-w-4xl lg:max-w-4xl sm:max-h-[85vh] md:max-h-[90vh] sm:rounded-lg overflow-hidden">
            {/* External close button for mobile - only visible on small screens */}
            {/* <button
              className="absolute top-2 right-2 sm:hidden z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              onClick={handleCloseVoice}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button> */}
            <div className="w-full h-full">
              <VoicePreview
                chatbotId={customizationData?.kb_id}
                voiceId={!isPreviewMode ? customizationData?.ai_voice_agent_id : 'a56a7938-bd6b-44b4-8e52-2c652946d528'}
                welcomeMessage={!isPreviewMode ? customizationData?.initial_messages.map(message => message.content).join(' ') : `I'm your AI Voice Assistant, fully trained on your chatbot data. Ask me anything connected to your chatbot data, and I'll provide the answers. Let's begin! ${customizationData?.initial_messages?.map(msg => msg.content).join(' ')}`}
                personalityPrompt={!isPreviewMode ? customizationData?.personality_prompt : ''}
                authToken={isInApp ? auth?.access_token : !isPreviewMode ? urlParams.get('token') : ''}
                conversationId={currentConversation?.id}
                onClose={handleCloseVoice}
                endpointType={isInApp || isPreviewMode ? WS_ENDPOINT_TYPES.BROWSER : WS_ENDPOINT_TYPES.SHARED}
              />
            </div>
          </div>
        </div>
      )}

      {showNewDesign ? (
        <V3Chat
        messages={messages}
        onSendMessage={handleSendQuestion}
        isLoading={isAnswerLoading}
        suggestedPrompts={suggestionPrompts || []}
        suggestedPromptsConfig={{
          position: config?.suggested_prompts_position || 'underLastMessage',
          kb_id: config?.kb_id || 'default',
          showOncePerKbId: config.show_prompt_messages,
          showSuggestionPrompts: showSuggestionPrompts,
          enableDragScroll: true,
          enableHoverScroll: true
        }}
        onPromptClick={(prompt, index) => {
          // Handle prompt click by sending the prompt as a message
          handleSendQuestion(prompt);
        }}
        handleMessagesOnChat={handleMessagesOnChat}
        isPreviewMode={isPreviewMode}
        isInHumanHandoverApp={isInHumanHandoverApp}
        isLiveAgentResolved={isLiveAgentResolved}
        pollResponse={pollResponse}
        isAnswerLoading={isAnswerLoading}
        config={{
          ...customizationData,
          prompt_suggestions: suggestionPrompts,
          notification,
        }}
        handleSendQuestion={handleSendQuestion}
        hiddenConversation={hiddenConversation}
        showInAppHeader={showInAppHeader}
        showMenuBtn={showMenuBtn}
        hiddenPoweredByDante={
          isDanteFaq || isInApp ? true : customizationData?.remove_watermark
        }
        hideFooter={hideFooter}
        handleOpenDanteConversations={handleOpenDanteConversations}
        handleOpenDanteFaq={handleOpenDanteFaq}
        isDanteFaq={isDanteFaq}
        showCloseBtn={showCloseBtn}
        handleCloseButton={handleCloseChat}
        isInApp={isInApp}
        handleOpenLiveAgent={handleOpenLiveAgent}
        showButtons={showButtons}
        dynamicButtons={dynamicButtons}
        setCookie={setCookie}
        handleFooterButton={handleFooterButton}
        showConsent={showConsent}
        setShowConsent={setShowConsent}
        hasMic={config?.has_mic}
        showSuggestionPrompts={showSuggestionPrompts}
        sources={sources}
        openSources={openSources}
        setOpenSources={setOpenSources}
        showSources={showSources}
        sourcesLoading={sourcesLoading}
        chatImageLoader={chatImageLoader}
        humanHandoverConfig={{
          isTaken:
            liveAgentConversation?.state !== 'waiting' &&
            liveAgentConversation?.state !== 'ai',
          isFlagged: liveAgentConversation?.is_flagged,
          isAssignedToCurrentUser: liveAgentConversation?.user_id === userData?.id
        }}
        humanHandoverTakeConversation={humanHandoverTakeConversation}
        humanHandoverMarkResolved={humanHandoverMarkResolved}
        setQuestionIsQuickResponse={setQuestionIsQuickResponse}
        interactingWithLiveAgent={interactingWithLiveAgent}
        setIsLiveAgentResolved={setIsLiveAgentResolved}
        messagesLoading={messagesLoading}
        handleShowVoice={handleShowVoice}
        isPreviousConversationHasLiveAgent={isPreviousConversationHasLiveAgent}
        showEmailTranscript={showEmailTranscript}
        setShowEmailTranscript={setShowEmailTranscript}
        currectConversationId={currentConversation?.id}
        liveAgentConversationId={(() => {
    
          return liveAgentConversation?.id;
        })()}
        setNotification={setNotification}
        isLeadGenMode={isLeadGenMode}
        onTypingStart={sendWebSocketTypingStart}
        onTypingStop={sendWebSocketTypingStop}
        isTyping={isWebSocketTyping}
        typingUser={webSocketTypingUser}
        isLoadingSuggestions={isLoadingSuggestions}
        leadsGenMode={isLeadGenMode}
        leadGenInputs={leadGenInputs}
      />
      ) : (
        <OldChat
          config={{
            ...customizationData,
            messages,
            notification,
          }}
          chatContainerRef={null}
          isInApp={isInApp}
          isPreviewMode={isPreviewMode}
          showInAppHeader={showInAppHeader}
          isDanteFaq={isDanteFaq}
          handleSendQuestion={handleSendQuestion}
          hiddenConversation={hiddenConversation}
          hiddenPoweredByDante={isDanteFaq || isInApp ? true : customizationData?.remove_watermark}
          handleCloseButton={handleCloseChat}
          handleOpenDanteConversations={handleOpenDanteConversations}
          handleOpenDanteFaq={handleOpenDanteFaq}
          handleOpenLiveAgent={handleOpenLiveAgent}
          showCloseBtn={showCloseBtn}
          showMenuBtn={showMenuBtn}
          isAnswerLoading={isAnswerLoading}
          showButtons={showButtons}
          dynamicButtons={dynamicButtons}
          hideFooter={hideFooter}
          handleFooterButton={handleFooterButton}
          showConsent={showConsent}
          setShowConsent={setShowConsent}
          hasMic={config?.has_mic}
          showSuggestionPrompts={showSuggestionPrompts}
          isLoadingSuggestions={isLoadingSuggestions}
          sources={sources}
          openSources={openSources}
          setOpenSources={setOpenSources}
          showSources={showSources}
          sourcesLoading={sourcesLoading}
          chatImageLoader={chatImageLoader}
          isInHumanHandoverApp={isInHumanHandoverApp}
          humanHandoverConfig={{
            isTaken:
              liveAgentConversation?.state !== 'waiting' &&
              liveAgentConversation?.state !== 'ai',
            isFlagged: liveAgentConversation?.is_flagged,
            isAssignedToCurrentUser: liveAgentConversation?.user_id === userData?.id
          }}
          humanHandoverMarkResolved={humanHandoverMarkResolved}
          humanHandoverTakeConversation={humanHandoverTakeConversation}
          setQuestionIsQuickResponse={setQuestionIsQuickResponse}
          interactingWithLiveAgent={interactingWithLiveAgent}
          isLiveAgentResolved={isLiveAgentResolved}
          pollResponse={pollResponse}
          messagesLoading={messagesLoading}
          handleShowVoice={handleShowVoice}
          isPreviousConversationHasLiveAgent={isPreviousConversationHasLiveAgent}
          showEmailTranscript={showEmailTranscript}
          setShowEmailTranscript={setShowEmailTranscript}
          currectConversationId={currentConversation?.id}
          setNotification={setNotification}
          isTyping={isWebSocketTyping}
          typingUser={webSocketTypingUser}
          onTypingStart={sendWebSocketTypingStart}
          onTypingStop={sendWebSocketTypingStop}
          isLeadGenMode={isLeadGenMode}
          leadGenInputs={leadGenInputs}
          handleLeadGenOptionClick={(option) => handleSendQuestion(option)}
          liveAgentConversation={liveAgentConversation}
        />
      )}
    </>
  );
});

ChatManager.propTypes = {
  config: PropTypes.shape({
    initial_messages: PropTypes.array,
    messages: PropTypes.array,
    has_mic: PropTypes.bool,
    show_calendly_at_the_start: PropTypes.bool,
    calendly_integration_enabled: PropTypes.bool,
    show_data_collection_on_conversation_begin: PropTypes.bool,
    data_collection: PropTypes.bool,
    show_live_agent_on_conversation_begin: PropTypes.bool,
    talk_to_live_agent: PropTypes.bool,
    notification: PropTypes.object,
    kb_id: PropTypes.string,
    token: PropTypes.string,
    prompt_suggestions: PropTypes.array,
    suggested_prompts_enabled: PropTypes.bool,
    data_collection_consent_text: PropTypes.string,
    remove_watermark: PropTypes.bool,
  }),
  hiddenConversation: PropTypes.bool,
  showInAppHeader: PropTypes.bool,
  showMenuBtn: PropTypes.bool,
  isInApp: PropTypes.bool,
  isDanteFaq: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  handleOpenDanteConversations: PropTypes.func,
  handleOpenDanteFaq: PropTypes.func,
  handleCloseButton: PropTypes.func,
  hiddenLlmSelector: PropTypes.bool,
  initialShouldFetchCustomization: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  cookie: PropTypes.string,
  setCookie: PropTypes.func,
  forceRefetch: PropTypes.bool,
  sources: PropTypes.array,
  openSources: PropTypes.bool,
  setOpenSources: PropTypes.func,
  promptToSend: PropTypes.string,
  onPromptProcessed: PropTypes.func,
  webSocketHook: PropTypes.object,
  onNewConversationCreated: PropTypes.func,
  isDirectLink: PropTypes.bool,
};

export default ChatManager;
